// FOLIO COMO EN RANDOM (pedido de Rodrigo, 2026-10-13): en la factura/boleta/guía (directa o del punto
// de venta) el campo muestra el correlativo que corresponde; se puede escribir OTRO número y con Enter
// se llama ese documento ya emitido para verlo, imprimirlo o sacar el PDF.
//
// El número digitado SOLO LLAMA un documento: la emisión siempre toma el siguiente folio del CAF (un
// folio saltado a mano dejaría un hueco ante el SII). Primero se busca en lo emitido por el ERP; si
// no está, en el archivo de Random (la historia de MADECI SPA), que es donde están las facturas de hoy.
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api, guardarArchivo, pedirArchivo } from '../api/cliente';
import type { ProximoFolio } from '../api/consultas';
import { VerDocumentoRandom, type DocumentoRandomListado } from '../consultas/DocumentosRandom';
import { PanelError } from '../comun/Estados';
import { fecha, pesos, rut } from '../comun/formato';
import { Modal } from '../comun/Modal';
import { NOMBRE_TIPO_DTE } from '../shell/BuscadorGlobal';

/** El tipo SII y su documento en Random. */
const TIDO_RANDOM: Record<number, string> = { 33: 'FCV', 39: 'BLV', 52: 'GDV', 61: 'NCV' };

interface DteEncontrado {
  id: string;
  tipo_dte: number;
  folio: number | null;
  estado: string;
  estado_sii: string;
  monto_total: string | null;
  fecha_emision: string | null;
  origen_id: string;
  receptor_rut: string | null;
  receptor_razon_social: string | null;
}

type Llamado =
  | { tipo: 'erp'; doc: DteEncontrado }
  | { tipo: 'random'; id: string }
  | { tipo: 'no-existe'; folio: string };

/** El último folio de ese documento en Random (MADECI SPA): de ahí sigue el correlativo. */
function useUltimoDeRandom(tipoDte: number, habilitado: boolean) {
  const tido = TIDO_RANDOM[tipoDte];
  return useQuery({
    queryKey: ['historico-random', 'ultimo', tido],
    queryFn: async () =>
      (
        await api.get<{ items: DocumentoRandomListado[] }>('/historico/documentos', {
          query: { empresa: '02', tipo: tido, tamano: 1 },
        })
      ).items[0] ?? null,
    enabled: habilitado && !!tido,
    staleTime: 10 * 60_000,
    retry: false,
  });
}

const numeroDe = (folio: string) => Number(folio.replace(/\D/g, '')) || null;

export function CampoFolio({
  empresaId,
  tipoDte,
  proximo,
  cargando,
  alAbrirNota,
}: {
  empresaId: string;
  tipoDte: number;
  proximo: ProximoFolio | undefined;
  cargando: boolean;
  /** Abre la venta del documento en su pantalla (punto de venta o nota), con sus datos guardados. */
  alAbrirNota?: (notaId: string, titulo: string) => void;
}) {
  const nombre = NOMBRE_TIPO_DTE[tipoDte] ?? `DTE ${tipoDte}`;
  // Sin CAF en el ERP, el correlativo de referencia es el que sigue al último emitido en Random.
  const ultimo = useUltimoDeRandom(tipoDte, !cargando && !proximo?.folio);
  const ultimoNumero = ultimo.data ? numeroDe(ultimo.data.folio) : null;
  const siguiente = proximo?.folio ?? (ultimoNumero ? ultimoNumero + 1 : null);
  const [texto, setTexto] = useState('');
  useEffect(() => setTexto(''), [tipoDte]);
  const [buscando, setBuscando] = useState(false);
  const [llamado, setLlamado] = useState<Llamado | null>(null);
  const [error, setError] = useState<unknown>(null);

  const llamar = async () => {
    const folio = texto.replace(/\D/g, '');
    if (!folio) return;
    setBuscando(true);
    setError(null);
    try {
      const erp = await api.get<DteEncontrado[]>('/dte/documentos', {
        query: { empresaId, folio, tipoDte: String(tipoDte) },
      });
      const d = erp.find((x) => String(x.folio) === folio);
      // Lo emitido por el ERP se abre en SU pantalla (Rodrigo, 2026-10-23): la boleta del punto de
      // venta en el punto de venta, la factura de una nota en la nota; no en una ventana aparte.
      if (d && alAbrirNota) {
        setTexto('');
        alAbrirNota(d.origen_id, `${nombre} N° ${Number(folio).toLocaleString('es-CL')}`);
      } else if (d) setLlamado({ tipo: 'erp', doc: d });
      else {
        const tido = TIDO_RANDOM[tipoDte];
        const r = tido
          ? await api.get<{ items: DocumentoRandomListado[] }>('/historico/documentos', {
              query: { empresa: '02', tipo: tido, folio },
            })
          : { items: [] };
        const h = r.items[0];
        setLlamado(h ? { tipo: 'random', id: h.id } : { tipo: 'no-existe', folio });
      }
    } catch (e) {
      setError(e);
    } finally {
      setBuscando(false);
    }
  };

  const motivo = cargando
    ? '…'
    : proximo?.folio
      ? 'próximo · se asigna al emitir'
      : siguiente
        ? `sigue a Random (último N° ${ultimoNumero!.toLocaleString('es-CL')}) · ${
            proximo?.motivo === 'SIN_EMISOR'
              ? 'en esta instancia no se emite'
              : 'falta cargar los folios (CAF) en el ERP'
          }`
        : 'sin folios (CAF) cargados';
  return (
    <span className="folio campo-folio" data-folio={siguiente ?? ''}>
      <label>
        Folio{' '}
        <input
          className="mono entrada-folio"
          aria-label={`Folio ${nombre}`}
          inputMode="numeric"
          placeholder={siguiente ? siguiente.toLocaleString('es-CL') : '—'}
          title="El correlativo que corresponde. Escribe otro número y Enter para llamar ese documento (ver, imprimir, PDF)"
          value={texto}
          onChange={(e) => setTexto(e.target.value.replace(/[^\d.]/g, ''))}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void llamar();
            } else if (e.key === 'Escape') setTexto('');
          }}
        />
      </label>
      {/* El detalle técnico (sigue a Random, faltan los CAF) queda al pasar el mouse (2026-10-16). */}
      <span className="ayuda" title={motivo}>
        {buscando ? 'buscando…' : siguiente && !proximo?.folio ? '' : motivo}
      </span>
      {!!error && <PanelError error={error} />}
      {llamado?.tipo === 'random' && (
        <VerDocumentoRandom id={llamado.id} alCerrar={() => setLlamado(null)} />
      )}
      {llamado?.tipo === 'erp' && (
        <VerDocumentoErp
          doc={llamado.doc}
          alCerrar={() => setLlamado(null)}
          {...(alAbrirNota ? { alAbrirNota: (id: string) => alAbrirNota(id, nombre) } : {})}
        />
      )}
      {llamado?.tipo === 'no-existe' && (
        <Modal titulo={`${nombre} N° ${llamado.folio}`} alCerrar={() => setLlamado(null)}>
          <p>
            No hay {nombre.toLowerCase()} N° {Number(llamado.folio).toLocaleString('es-CL')} emitida
            (ni en el ERP ni en Random).
          </p>
          <div className="acciones-modal">
            <button type="button" className="primario" onClick={() => setLlamado(null)}>
              Volver
            </button>
          </div>
        </Modal>
      )}
    </span>
  );
}

/** Un documento emitido por el ERP: sus datos y el PDF (original o cedible) y el XML. */
function VerDocumentoErp({
  doc,
  alCerrar,
  alAbrirNota,
}: {
  doc: DteEncontrado;
  alCerrar: () => void;
  alAbrirNota?: (notaId: string) => void;
}) {
  const [error, setError] = useState<unknown>(null);
  const nombre = NOMBRE_TIPO_DTE[doc.tipo_dte] ?? `DTE ${doc.tipo_dte}`;
  const archivo = async (que: 'pdf' | 'xml', ejemplar = 'ambos') => {
    setError(null);
    try {
      const b = await pedirArchivo(
        'GET',
        `/dte/documentos/${doc.id}/${que}`,
        que === 'pdf' ? { query: { ejemplar } } : {},
      );
      if (que === 'pdf') window.open(URL.createObjectURL(b), '_blank', 'noopener');
      else guardarArchivo(b, `DTE${doc.tipo_dte}-${doc.folio ?? doc.id.slice(0, 8)}.xml`);
    } catch (e) {
      setError(e);
    }
  };
  return (
    <Modal
      titulo={`${nombre} N° ${doc.folio?.toLocaleString('es-CL') ?? '—'}`}
      alCerrar={alCerrar}
      pie={
        <>
          <button type="button" onClick={alCerrar}>
            Cerrar
          </button>
          {alAbrirNota && (
            <button type="button" onClick={() => alAbrirNota(doc.origen_id)}>
              Ver la nota
            </button>
          )}
          <button type="button" onClick={() => void archivo('xml')}>
            XML
          </button>
          {doc.tipo_dte !== 39 && doc.tipo_dte !== 41 && (
            <button type="button" onClick={() => void archivo('pdf', 'cedible')}>
              PDF cedible
            </button>
          )}
          <button type="button" className="primario" onClick={() => void archivo('pdf')}>
            Imprimir / PDF
          </button>
        </>
      }
    >
      <dl className="datos-ficha">
        <dt>Cliente</dt>
        <dd>
          {doc.receptor_razon_social ?? '—'}
          {doc.receptor_rut ? ` · ${rut(doc.receptor_rut)}` : ''}
        </dd>
        <dt>Emisión</dt>
        <dd>{doc.fecha_emision ? fecha(doc.fecha_emision) : '—'}</dd>
        <dt>Total</dt>
        <dd>{pesos(doc.monto_total)}</dd>
        <dt>Estado</dt>
        <dd>
          {doc.estado} · SII {doc.estado_sii}
        </dd>
      </dl>
      {!!error && <PanelError error={error} />}
    </Modal>
  );
}
